import { create } from "zustand";

/**
 * Playback state. The clock lives here rather than in React state because the
 * canvas reads it sixty times a second; components subscribe to coarse slices
 * of it so they re-render at human speed, not frame speed.
 */
export interface PlaybackState {
  round: number;
  timeMs: number;
  playing: boolean;
  speed: number;
  focusPawn: number | null;
  /** Bounds of the round being played, in match time. */
  startMs: number;
  endMs: number;

  /** Selects a round and, optionally, the instant to start at.
   *
   *  Round, bounds and playhead move together in one update on purpose: an
   *  effect that set the bounds separately ran with the previous round's values
   *  and clamped the playhead back to the start. */
  setRound: (round: number, startMs: number, endMs: number, atMs?: number) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setSpeed: (speed: number) => void;
  seek: (timeMs: number) => void;
  advance: (deltaMs: number) => void;
  focus: (pawn: number | null) => void;
}

export const SPEEDS = [0.25, 0.5, 1, 2, 4] as const;

export const usePlayback = create<PlaybackState>((set, get) => ({
  round: 1,
  timeMs: 0,
  playing: false,
  speed: 1,
  focusPawn: null,
  startMs: 0,
  endMs: 0,

  setRound: (round, startMs, endMs, atMs) =>
    set({
      round,
      startMs,
      endMs,
      timeMs: Math.min(Math.max(atMs ?? startMs, startMs), endMs),
      playing: false,
    }),
  play: () => {
    const s = get();
    // Restarting from the end plays the round again rather than doing nothing.
    if (s.timeMs >= s.endMs) set({ timeMs: s.startMs, playing: true });
    else set({ playing: true });
  },
  pause: () => set({ playing: false }),
  toggle: () => (get().playing ? get().pause() : get().play()),
  setSpeed: (speed) => set({ speed }),
  seek: (timeMs) =>
    set((s) => ({ timeMs: Math.min(Math.max(timeMs, s.startMs), s.endMs) })),
  advance: (deltaMs) =>
    set((s) => {
      const next = s.timeMs + deltaMs;
      if (next >= s.endMs) return { timeMs: s.endMs, playing: false };
      return { timeMs: next };
    }),
  focus: (pawn) => set((s) => ({ focusPawn: s.focusPawn === pawn ? null : pawn })),
}));

/** Subscribes to time at a coarse resolution so a component re-renders about
 *  ten times a second instead of sixty. */
export function useCoarseTime(resolutionMs = 100): number {
  // Never below the round's own start. Rounding down by a tenth of a second
  // otherwise steps behind it, and what a round opens with — the shields, the
  // charges and the ultimate points a player already had — is recorded at
  // exactly that instant and would be read as not yet true.
  return usePlayback((s) =>
    Math.max(s.startMs, Math.floor(s.timeMs / resolutionMs) * resolutionMs),
  );
}
